import { connect, ConnectedProps } from 'react-redux'
import InputTableLeftColumn, { InputTableLeftColumnCompleteRequiredProps } from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn'
import withLoading from '../../../../../../../../../../utils/hoc/withLoading'
import { ComponentTabObject } from '../../../../../../../../../../utils/hooks/useTabLogic'
import useTwinTranslation from '../../../../../../../../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../../../../../../../../utils/reducers'
import { CostBuyPayload } from '../../../../../../../../../../utils/reducers/company/costsBuy'
import BuyStepHeaderMainNoPermission from '../../../../../header/BuyStepHeaderMainNoPermission'
import BuyReadOnlyHeaderFinancialRows from '../../../../../header/BuyReadOnlyHeaderFinancialRows'
import BuyReadOnlyHeaderCosts from '../../../../../header/BuyReadOnlyHeaderCosts'
import BuyRealOnlyHeaderAccounts from '../../../../../header/BuyReadOnlyHeaderAccounts'
import useBuyDeliveryNoteHeaderURLs from '../../../../../../../../../../utils/hooks/buys/orders/useBuyDeliveryNoteHeaderURLs'
import BuyReadOnlyHeaderShipping from '../../../../../header/BuyReadOnlyHeaderShipping'
import { BuyTagsPayload } from '../../../../../../../../../../utils/reducers/buys/buyTags'
import BuyReadOnlyHeaderVariables from '../../../../../header/BuyReadOnlyHeaderVariables'
import { SellVariablePayload } from '../../../../../../../../../../utils/reducers/company/sellVariables'
import AppStyleDecider from '../../../../../../../../../../baseComponents/AppsStyleDecider'
import InputTableTopHeader from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableTopHeader'
import ITHeaderText from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components/ITHeaderText'
import { parseAddressToOneLine } from '../../../../../../../../../../utils/globals/address'
import { displayDate } from '../../../../../../../../../../utils/globals/date'
import { returnClassOfSpanTemplate } from '../../../../../../../../../../utils/globals/tailwind'
import { ITHeaderBuyOrderTagsNew } from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components'
import { BuyOrderModelType } from '@teinor/erp/types/company/order/buy/buyOrder'
import { FormElement, FormRender } from '../../../../../../../../../../forms/FormRenderer/types'
import { getActiveCurrenciesDictionary } from '../../../../../../../../../../utils/reducers/getters'

type BuyDeliveryNoteCreateNoPermissionStepITHeaderProps = InputTableLeftColumnCompleteRequiredProps & ReduxBuyDeliveryNoteCreateNoPermissionStepITHeader & {}

const BuyDeliveryNoteCreateNoPermissionStepITHeader: React.FC<BuyDeliveryNoteCreateNoPermissionStepITHeaderProps> = ({ ...rest }) => {
    return <AppStyleDecider DefaultComponent={BuyDeliveryNoteCreateNoPermissionStepITHeaderDefault} NewComponent={BuyDeliveryNoteCreateNoPermissionStepITHeaderNew} {...rest} />
}

const BuyDeliveryNoteCreateNoPermissionStepITHeaderDefault: React.FC<BuyDeliveryNoteCreateNoPermissionStepITHeaderProps> = ({ buyTags, ...rest }) => {
    const { t } = useTwinTranslation()
    const orderHeaderURLs = useBuyDeliveryNoteHeaderURLs()
    const tabs: ComponentTabObject = {
        'main': {
            component: BuyStepHeaderMainNoPermission,
            text: t('dataDeliveryNote', 'Datos del albarán'),
            extraProps: { buyTags }
        },
    }
    if(!rest.headerData?.hidePrices){
        tabs['financialRow'] = {
            component: BuyReadOnlyHeaderFinancialRows,
            text: t('discountsAndRecharges', 'Descuentos y recargos'),
            permission: 'buy.deliveryNote',
            extraProps: { orderHeaderURLs }
        }
        tabs['costs'] = {
            component: BuyReadOnlyHeaderCosts,
            text: t('costsBuy', 'Gastos de la compra'),
            permission: 'buy.deliveryNote',
            extraProps: { orderHeaderURLs }
        }
    }
    tabs['shipping'] = {
        component: BuyReadOnlyHeaderShipping,
        text: t('shipping', 'Transporte'),
        permission: 'buy.deliveryNote',
        extraProps: {
            orderHeaderURLs
        }
    }
    tabs['accounts'] = {
        component: BuyRealOnlyHeaderAccounts,
        text: t('divideIntoChapters', 'Dividir en capítulos'),
        permission: 'buy.deliveryNote',
        extraProps: { orderHeaderURLs }
    }
    tabs['variables'] = {
        component: BuyReadOnlyHeaderVariables,
        text: t('buyVariables', 'Variables de compra'),
        permission: 'buy.deliveryNote',
        extraProps: { orderHeaderURLs }
    }
    return (
        <InputTableLeftColumn tabs={tabs} {...rest} />
    )
}

const BuyDeliveryNoteCreateNoPermissionStepITHeaderNew: React.FC<BuyDeliveryNoteCreateNoPermissionStepITHeaderProps> = ({ headerData, buyTags, setCompleteData, ...rest }) => {
    const { t } = useTwinTranslation()
    const activeCurrencies = getActiveCurrenciesDictionary()

    let extraFields: FormElement<keyof BuyOrderModelType>[] = []
    if (headerData?.CustomerId) {
        extraFields.push({
            name: 'CustomerId',
            component: 'Custom',
            render: () => <ITHeaderText label={t('customer', 'Cliente')} value={headerData?.Customer?.name} />,
            hideSectionByDefault: true
        },)
    }

    const sections: FormRender<BuyOrderModelType> = [
        {
            cols: 7,
            className: 'gap-x-20',
            elements: [
                {
                    name: 'custom_name',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('customName', 'Nombre personalizado')} value={value} />
                },
                {
                    name: 'processed_date',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('dateBuyOrder', 'Fecha de la compra')} value={displayDate(new Date(value))} />
                },
                {
                    name: 'RepresentativeEmployeeId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('representative', 'Representante')} value={headerData?.RepresentativeEmployee?.fullname_short} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'OrderTags',
                    component: 'Custom',
                    render: ({ extraProps }) => {
                        return (<ITHeaderBuyOrderTagsNew selectedTags={extraProps.selectedTags} dictTags={buyTags} headerData={headerData || {}} onChange={async () => { }} {...rest} className={returnClassOfSpanTemplate(3) + ' order_top_header_custom_select_tags'} readOnly={true} />)
                    },
                    extraProps: { selectedTags: headerData?.tags },
                    hideSectionByDefault: true
                },
                {
                    name: 'full_assigned_number',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('assignedNumber', 'Nº asignado')} value={value} className={(headerData?.assigned_number ? '' : 'text-red-BA')} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'supplier_number_order',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('supplierNumber', 'Número del proveedor')} value={value} />,
                },
                {
                    name: 'supplier_assigned_number',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('supplierInvoiceS', 'Fact. proveedor')} value={value} />,
                },
                {
                    name: 'supplier_date',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('dateSupplier', 'Fecha del proveedor')} value={value ? displayDate(new Date(value)) : ''} />,
                },
                ...extraFields,
                {
                    name: 'BillingWorkingCenterId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('billedBy', 'Facturado por')} value={headerData?.BillingWorkingCenter?.name || ''} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'WorkingCenterCounterId',
                    component: 'Custom',
                    render: () => {
                        return <ITHeaderText label={t('serie', 'Serie')} value={headerData?.WorkingCenterCounter?.serie} />
                    },
                    hideSectionByDefault: true
                },
                {
                    name: 'WorkingCenterId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('buyedBy', 'Comprado por')} value={headerData?.WorkingCenter.name || ''} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'WarehouseId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('warehouse', 'Almacen')} value={headerData?.Warehouse?.name || ''} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'BillingAddressId',
                    component: 'Custom',
                    render: () => {
                        return <ITHeaderText label={t('workingCenterAddress', 'Dirección centro de trabajo')} value={parseAddressToOneLine(headerData?.BillingWorkingCenter)
                        } />
                    },
                    hideSectionByDefault: true
                },
                {
                    name: 'CurrencyId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('currency', 'Moneda')} value={activeCurrencies?.[value]?.name} />,
                    hideSectionByDefault: true
                },
            ]
        }
    ]

    return (
        <InputTableTopHeader handleChange={async () => { }} sections={sections} headerData={headerData} getCompleteData={() => { }} />
    )
}

const BuyDeliveryNoteCreateNoPermissionStepITHeaderDispatch = {
    setBuyTags: (payload: BuyTagsPayload) => ({ type: 'CHANGE_BUY_TAGS', payload }),
    setCostsBuy: (payload: CostBuyPayload) => ({ type: 'CHANGE_COSTSBUY', payload }),
    setSellVariables: (payload: SellVariablePayload) => ({ type: 'CHANGE_SELLVARIABLE', payload }),
}

export type ReduxBuyDeliveryNoteCreateNoPermissionStepITHeader = ConnectedProps<typeof buyDeliveryNoteCreateStepHeaderConnect>
const mapBuyDeliveryNoteCreateStepHeaderConnector = (state: AllReduxPayloads) => ({ costsBuy: state.costsBuy, buyTags: state.buyTags, sellVariables: state.sellVariables })
const buyDeliveryNoteCreateStepHeaderConnect = connect(mapBuyDeliveryNoteCreateStepHeaderConnector, BuyDeliveryNoteCreateNoPermissionStepITHeaderDispatch)
const buyDeliveryNoteCreateStepHeaderConnectLoading = withLoading(BuyDeliveryNoteCreateNoPermissionStepITHeader, [{ fetchUrl: '/api/app/config/cost/costBuy/getAllCostBuys', propName: 'costsBuy', setFunctionName: 'setCostsBuy' }, { fetchUrl: '/api/app/order/buy/tag/getAllBuyTags', propName: 'buyTags', setFunctionName: 'setBuyTags' }, { fetchUrl: '/api/app/config/sell/sellVariable/getAllSellVariables', propName: 'sellVariables', setFunctionName: 'setSellVariables' }])
const BuyDeliveryNoteCreateNoPermissionStepITHeaderConnect = buyDeliveryNoteCreateStepHeaderConnect(buyDeliveryNoteCreateStepHeaderConnectLoading)

export default BuyDeliveryNoteCreateNoPermissionStepITHeaderConnect